import {
    ActivationPayload,
    CrcDecision,
    CrcFormPayload,
    DashboardNotifications,
    ProductCode,
} from '@dev/ee-api/ee-api-models';
import { Alert, SimpleLoader } from '@dev/ee-components';
import { BillingPaths, ContactsPaths, HomePaths, SettingsPaths, TemplatesPaths } from '@/services/dictionaries/Paths';
import {
    CrcValidate,
    GetAccountWarnings,
    GetAccountsNameStart,
    LoginWithDisposableToken,
    PurchasePrivateIP,
    SendCrcForm,
    UpdateCRCFormParam,
} from '@/store/account/actions';
import { HistoryBackActionContext, useHistoryBackAction } from '@/services/hooks/useHistoryBackAction';
import { HotJarEvents, useHotJar } from '@/services/hooks/useHotjar';
import { LoadPaymentProviders, getProviders } from '@/store/payments/actions';
import React, { FC, useEffect } from 'react';
import {
    UpdateDashboardSeenNotification,
    getDashboardSeenNotificationsStore,
} from '@/store/dashboardSeenNotifications/actions';
import { connect, useDispatch, useSelector } from 'react-redux';
import { DeletePurchaseInfo } from '@/store/pools/actions';
import { DiscountBanner } from '@/services/Layout/DiscountBanner';
import { Home as HomeModule } from '@dev/ee-modules';
import { DPAUpdatedBanner } from '@/services/Layout/DPABanner';
import { LoadDomains } from '@/store/domain/actions';
import { LoadInboundRoutes } from '@/store/inboundRoutes/actions';
import { LoadWebhooks } from '@/store/webhooks/actions';
import { LoginWithDisposableTokenPayload } from '@/store/account/types';
import { ResendActivationEmail } from '@/store/auth/actions';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { ShowToast } from '@/store/toast/actions';
import { ShowToastParams } from '@/store/toast/types';
import { ApplicationState as State } from '@/store';
import { StripePolicyChangeAlert } from '@/services/Layout/Alerts/StripyPolicyChangeAlert';
import { apiPricingPlans } from '@/services/dictionaries/ApiPricingPlans';
import { compose } from 'react-recompose';
import { getConfigStore } from '@/store/config/actions';
import { getNotificationStore } from '@/store/notifications/actions';
import { isIntercomEnabled } from '@/services/Secuirty';
import { isNil } from 'lodash-es';
import { links } from '@/services/dictionaries/links';
import { pricingWithAutomation } from '@/services/dictionaries/PricingPlanWithAutomation';
import { pricingsWithEnabledNotifications } from '@/services/dictionaries/PricingsWithEnabledNotifications';
import { push } from 'redux-first-history';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { useIntercom } from 'react-use-intercom';
import { useNotificationCenter } from '@/services/hooks/useNotificationCenter';
import { useReshardingActions } from '@/services/hooks/useReshardingActions';
import { withTranslation } from 'react-i18next';
import { LoadLandingPageSubdomain } from '@/store/landingPageSubdomain/actions';
import { getCampaignsStore } from '@/store/campaigns/actions';
import { useConfigProductCode } from '@/services/hooks/useConfigProductCode';
import { useRequireOnboarding } from '../Onboarding/hooks/useRequireOnboarding';
import ReactivationForm from './CRC';
import PLACEHOLDER_IMAGE from '../../images/placeholder.svg';
import { Onboarding } from '../Onboarding';
import { HomeProps } from './HomeInterface';
import { AlertWithCenteredMessage } from './styled';
import Preview from '../Campaigns/Preview';
import { StartCampaignCreator } from '../CampaignCreator/Start';
import CampaignDeleteDialog from '../Campaigns/CampaignDeleteDialog';
import { CreatorHomePage } from './components/CreatorHomePage';
import { findPersonaSurveyNotificationName } from '@/services/Dialog/personaSurveyDialog/tools/findPersonaSurveyNotificationName';

const mapState = (state: State, ownProps: HomeProps): HomeProps => ({
    ...ownProps,
    account: state.account,
    domain: state.domain,
    browser: state.browser,
    webhooks: state.webhooks,
    inbound: state.inbound,
    config: state.config,
    products: state.products,
    payments: state.payments,
});

const dispatchProps = {
    LoadDomains,
    GetAccountWarnings,
    Push: push,
    UpdateCRCFormParam,
    SendCrcForm,
    CrcValidate,
    GetAccountsNameStart,
    LoadWebhooks,
    LoadInboundRoutes,
    ShowDialog,
    DeletePurchaseInfo,
    PurchasePrivateIP,
    LoginWithDisposableToken,
    ResendActivationEmail,
    ShowToast,
};

interface DispatchProps {
    LoadDomains(): void;
    Push(url: string): void;
    GetAccountWarnings(): void;
    UpdateCRCFormParam(params: CrcFormPayload): void;
    SendCrcForm(): void;
    CrcValidate(decision: CrcDecision): void;
    GetAccountsNameStart(): void;
    LoadWebhooks(): void;
    LoadInboundRoutes(): void;
    ShowDialog(params: ShowDialogParams): void;
    DeletePurchaseInfo(): void;
    PurchasePrivateIP(params: Record<string, any>): void;
    LoginWithDisposableToken(params: LoginWithDisposableTokenPayload): void;
    ResendActivationEmail(payload: ActivationPayload): void;
    ShowToast(params: ShowToastParams): void;
}

type ExtendedProps = HomeProps & DispatchProps;

const domainAuthenticationRequirementsTargetDate = new Date('2024-01-29 14:30:00+01').getTime();

// eslint-disable-next-line sonarjs/cognitive-complexity
const Home: FC<ExtendedProps> = (props: ExtendedProps): JSX.Element => {
    const { boot, show } = useIntercom() || {};
    const { isPilotPlan, isActive } = useCurrentPlanStatus();
    const dispatch = useDispatch();
    const campaigns = useSelector(getCampaignsStore);
    const productCode = useConfigProductCode();

    const notifications = useSelector(getNotificationStore);
    const { showNotificationDialog, showNotificationBanner } = useNotificationCenter();
    const providers = useSelector(getProviders);
    const { isStandalone, bootedAsChild, routerBasePath } = useSelector(getConfigStore);
    const { list: dashboardSeenNotifications, isLoaded: dashboardSeenNotificationsLoaded } = useSelector(
        getDashboardSeenNotificationsStore
    );
    const { showSurvey } = useHotJar();

    const isSavedRef = React.useRef(true);

    const historyBackAction = useHistoryBackAction({
        isSavedRef,
    });

    useEffect(() => {
        dispatch(LoadLandingPageSubdomain());
        isIntercomEnabled() && boot();
        if (!providers) dispatch(LoadPaymentProviders());
    }, []);
    
    useEffect(() => {
        if (isNil(isActive)) return;
        showSurvey(HotJarEvents.PaidUsersMarketing);
        showSurvey(HotJarEvents.FreeUsersMarketing);
        showSurvey(HotJarEvents.FreeUsersMarketingZeroSent);
        showSurvey(HotJarEvents.NPSEmailMarketing);
    }, [isActive]);

    const launchIntercom = () => {
        isIntercomEnabled() && show();
    };

    if (bootedAsChild) {
        if (!HomePaths.AutomationNew.includes(routerBasePath)) {
            HomePaths.AutomationNew = `${routerBasePath}${HomePaths.AutomationNew}`;
        }
    } else {
        if (!HomePaths.AutomationNew.includes('#')) {
            HomePaths.AutomationNew = `#${HomePaths.AutomationNew}`;
        }
    }

    const { checkReshardStatusIfNeeded, reshardHomeAlertConfig } = useReshardingActions();
    //reshard alert info
    useEffect(() => {
        checkReshardStatusIfNeeded();
    }, []);

    //TLS modal
    useEffect(() => {
       // if (!notifications?.notificationList) return;

        const personaSurveryNotification = findPersonaSurveyNotificationName(notifications.notificationList);
        if (personaSurveryNotification) {
            showNotificationDialog(personaSurveryNotification);
        }
        // showNotificationDialog(DashboardNotifications.TLSDicontinueNotification);
    }, [notifications?.notificationList?.length, dashboardSeenNotifications?.length]);

    const closeAuthenticationRequirementsModal = (): void => {
        dispatch(UpdateDashboardSeenNotification(DashboardNotifications.DMARCNotification));
    };

    const goToDomainsAuthenticationRequirementsModal = (): void => {
        dispatch(UpdateDashboardSeenNotification(DashboardNotifications.DMARCNotification));
        dispatch(push(SettingsPaths.Domains));
    };
    const domainAuthenticationRequirementsIsOpen =
        dashboardSeenNotificationsLoaded &&
        !dashboardSeenNotifications?.includes(DashboardNotifications.DMARCNotification);

    const {
        isPossiblyRequired: isOnboardingPossiblyRequired,
        isRequired: isOnboardingRequired,
        isLoading: isOnboardingLoading,
    } = useRequireOnboarding();

    if (isOnboardingPossiblyRequired && isOnboardingLoading) {
        return <SimpleLoader />;
    }

    if (isOnboardingRequired) {
        return <Onboarding />;
    }

    const showStripePolicyAlert = showNotificationBanner(DashboardNotifications.StripePolicyChange);
    const isCreatorProduct = productCode === ProductCode.CreatorSuite;

    return (
        <HistoryBackActionContext.Provider value={historyBackAction}>
            {isCreatorProduct && <CreatorHomePage />}
            {!isCreatorProduct && <HomeModule
                {...(props as any)}
                links={links}
                paths={{ ...SettingsPaths, ...ContactsPaths, ...HomePaths, ...TemplatesPaths, ...BillingPaths }}
                apiPricingPlans={apiPricingPlans}
                pricingWithAutomation={pricingWithAutomation}
                crcForm={ReactivationForm}
                launchIntercom={launchIntercom}
                plansWithEnabledNotifications={pricingsWithEnabledNotifications}
                welcomeDialog={{
                    image: <PLACEHOLDER_IMAGE />,
                }}
                topBanner={isStandalone && !isPilotPlan && <DiscountBanner />}
                productCode={(isStandalone || bootedAsChild) && ProductCode.Marketing}
                showChangeProduct={false}
                customNotifications={
                    <>
                        {showStripePolicyAlert && (
                            <AlertWithCenteredMessage>
                                <StripePolicyChangeAlert />
                            </AlertWithCenteredMessage>
                        )}
                        {reshardHomeAlertConfig && (
                            <AlertWithCenteredMessage>
                                <Alert {...(reshardHomeAlertConfig as any)} />
                            </AlertWithCenteredMessage>
                        )}
                        <DPAUpdatedBanner />
                    </>
                }
                dialogsSettings={{
                    domainAuthenticationRequirements: {
                        targetTimestamp: domainAuthenticationRequirementsTargetDate,
                        open: domainAuthenticationRequirementsIsOpen,
                        closeCallback: closeAuthenticationRequirementsModal,
                        confirmCallback: goToDomainsAuthenticationRequirementsModal,
                    },
                }}
            />}
            {campaigns.currentPreview.campaign && campaigns.currentPreview.templates?.length && <Preview />}
            {campaigns.isCampaignCreatorOpen && <StartCampaignCreator />}
            <CampaignDeleteDialog />
        </HistoryBackActionContext.Provider>
    );
};

export default compose(withTranslation(), connect<HomeProps, DispatchProps>(mapState, dispatchProps))(Home);

